import { SIMULATION_ENVIRONMENT } from "./isaacEvaluationAvailability";
import {
  SCHEDULE_SUBMIT_STEPS,
  SUBMIT_STEPS,
  type SubmitInitState,
  type SubmitStep,
} from "./submitWizardForm";

/**
 * 제출 위저드의 두 모드("새 Training" / "새 Schedule")의 차이를 이 표 하나가 소유한다.
 *
 * 두 모드는 같은 스텝·같은 폼·같은 제출 payload 를 쓰고, Schedule 모드만 몇 가지를 끄거나 바꾼다.
 * 그 차이를 소비처마다 `mode === "schedule"` 로 적으면 "Schedule 에서 무엇이 다른가"를 확인하려고
 * 파일 네 개를 읽어야 하고, 축이 늘 때마다 같은 조건이 하나씩 더 생긴다. 소비처는 `features.<축>` 만
 * 읽고, 새 차이는 여기에 한 줄로 추가한다.
 *
 * Owns: 창 문구, 스텝 목록, 목록 출처(내 것/공용), Dataset 환경 한정, Commit 고정 가능 여부,
 * 고를 수 있는 시작점. 스텝 목록에 Schedule 이 있는지(`hasScheduleStep`)가 제출 API 분기·초안
 * 준비 게이트·admin 권한 요구를 함께 정한다. 셋은 모두 "이 위저드가 스케줄을 만드는가"라는 같은
 * 판정이라 결과가 달라지면 안 된다.
 * Does not own: 초안 자체의 규칙(`scheduleDraft`), 역할 판정(`canManageSchedules`).
 */
export type SubmitWizardMode = "session" | "schedule";

export type SubmitWizardFeatures = {
  windowTitle: string;
  submitLabel: { idle: string; busy: string };
  // 스텝 목록. 반복 축의 소유 여부도 여기서 나온다(`hasScheduleStep`). 같은 사실을 boolean 으로
  // 한 번 더 적으면, 스텝 없이 초안 게이트만 켜진 행을 타입이 막지 못한다. 그 위저드는 채울 수
  // 없는 초안을 요구해서 '다음'이 영구히 꺼진다.
  steps: readonly SubmitStep[];
  // 이 위저드가 내 것을 다루는지 그룹 공용을 다루는지. workspace·experiment 목록 출처,
  // 공용 Workspace 드롭다운, 실험 생성 뒤 목록 갱신 방식, "내 것이 아님" 문구를 이 한 값이 정한다.
  ownership: "mine" | "shared";
  // Dataset 라벨 `environment` 한정. null 이면 제한이 없다. picker 목록 좁힘과 제출 게이트를
  // 함께 소유한다. 좁히기만 하고 게이트가 없으면 북마크 같은 다른 출처로 그대로 통과한다.
  // 값은 simulation 하나로 제한한다. 게이트 술어가 `hasSimulationDataset` 로 고정이라, `string` 으로
  // 열어 두고 다른 환경을 적으면 picker 는 그 환경을 보여 주고 게이트는 simulation 을 요구한다.
  // 그러면 Dataset 스텝이 영구히 열리지 않는다. 환경을 추가할 때 이 타입 때문에 게이트도 함께 고치게 된다.
  datasetEnvironment: typeof SIMULATION_ENVIRONMENT | null;
  // Branch 위에 Commit 을 고정할 수 있는지. 끄면 Commit 필드를 표시하지 않고, 커밋 목록도 받지
  // 않으며, 확정 요약은 Branch 이름만 표시한다.
  commitPin: boolean;
  // 고를 수 있는 시작점. "all" 이면 제한이 없고, 좁히는 행은 사유를 함께 적어야 한다. 둘을
  // 따로 두면 사유 없이 좁힌 행이 라디오를 비활성으로 두고 그 아래에 활성일 때의 설명을
  // 남긴다. 그러면 사용자는 고를 수 없는 이유를 알 수 없다.
  initScope: "all" | { strategies: readonly SubmitInitState["strategy"][]; lockReason: string };
};

// Schedule 은 Branch 의 최신 코드를 반복해 학습하는 축이라 시작점이 scratch 하나다. Policy 나
// Checkpoint 처럼 고정된 Weights 에서 시작하면 매 실행이 같은 학습을 반복한다.
const SCHEDULE_INIT_LOCK_REASON =
  "Schedule은 Branch의 최신 코드를 처음부터 반복해 학습합니다. 다른 시작점이 필요하면 새 Training으로 제출해 주세요.";

const SESSION_FEATURES: SubmitWizardFeatures = {
  windowTitle: "새 Training을 제출합니다.",
  submitLabel: { idle: "제출", busy: "제출 중…" },
  steps: SUBMIT_STEPS,
  ownership: "mine",
  datasetEnvironment: null,
  commitPin: true,
  initScope: "all",
};

// Schedule 은 admin 이 그룹 공용 Workspace 에 만든다. eval 추종·Isaac Evaluation 이
// 전제라 시뮬레이션 Dataset 만 받는다.
const SCHEDULE_FEATURES: SubmitWizardFeatures = {
  windowTitle: "새 Schedule을 만듭니다.",
  submitLabel: { idle: "Schedule 생성", busy: "생성 중…" },
  steps: SCHEDULE_SUBMIT_STEPS,
  ownership: "shared",
  datasetEnvironment: SIMULATION_ENVIRONMENT,
  commitPin: false,
  initScope: { strategies: ["scratch"], lockReason: SCHEDULE_INIT_LOCK_REASON },
};

// 삼항이 아니라 조회를 쓴다. mode 가 늘면 컴파일러가 새 행을 요구한다. 삼항으로 두면 새 mode 가
// 알림 없이 session 행(Commit 고정 열림·내 것·시작점 3종)을 받아서 모든 축이 fail-open 이 된다.
const FEATURES_BY_MODE: Record<SubmitWizardMode, SubmitWizardFeatures> = {
  session: SESSION_FEATURES,
  schedule: SCHEDULE_FEATURES,
};

export function submitWizardFeatures(mode: SubmitWizardMode): SubmitWizardFeatures {
  return FEATURES_BY_MODE[mode];
}

// 이 위저드가 반복 축을 소유하는지. 확정 요약의 반복 행, 초안 준비 게이트, 제출 API 분기,
// admin 권한 요구가 모두 이 한 판정을 읽는다.
export function hasScheduleStep(features: SubmitWizardFeatures): boolean {
  return features.steps.includes("schedule");
}

// 이 시작점이 잠겼으면 그 사유, 아니면 null. 잠금과 문구가 한 자리에서 나와야 "비활성인데
// 사유가 없는" 라디오가 생기지 않는다.
export function initStrategyLockReason(
  features: SubmitWizardFeatures,
  strategy: SubmitInitState["strategy"],
): string | null {
  const scope = features.initScope;
  if (scope === "all" || scope.strategies.includes(strategy)) {
    return null;
  }
  return scope.lockReason;
}
